import { useCallback, useEffect, useRef, useState } from 'react';
import { GridApi, RowDragEndEvent, RowDragEvent } from 'ag-grid-community';
import { useAppDispatch } from '../../../hooks';
import { countNodesByRouteName } from '../utils';
import { showRoutesAssignDialog } from '../../../state/ui/dialog';
import TranslationHelper from '../../../helpers/TranslationHelper';

export const usePlanningDragAndDrop = (
    routesGridApi: GridApi | undefined,
    tasksGridApi: GridApi | undefined
) => {
    const dispatch = useAppDispatch();
    const [highlightedRow, setHighlightedRow] = useState<string | null>(null);
    const highlightedRowRef = useRef<string | null>(null);

    const onDragStop = useCallback(
        (params: RowDragEndEvent) => {
            setHighlightedRow(null);
            highlightedRowRef.current = null;
            const draggableTasks = params.nodes.filter((node) => {
                return node.data.draggable;
            });
            const tasksToAssign = countNodesByRouteName(draggableTasks);
            const warning = params.nodes.length !== draggableTasks.length;
            if (!params?.overNode?.data) {
                highlightedRowRef.current = null;
                setHighlightedRow(null);
                routesGridApi?.redrawRows();
                return;
            }
            const { name, id } = params.overNode.data;
            routesGridApi?.redrawRows();
            dispatch(
                showRoutesAssignDialog({
                    tasks: tasksToAssign,
                    target: { name, id },
                    warning,
                })
            );
        },
        [tasksGridApi]
    );

    const onRowDragMove = useCallback(
        (event: RowDragEvent) => {
            const isDraggable = event.node.data?.draggable;
            const overNode = event.overNode?.data;
            const isOutOfDropZone =
                highlightedRow && !highlightedRowRef.current;

            if (!overNode || !isDraggable) {
                event.api.clearFocusedCell?.();
                return;
            }

            if (isOutOfDropZone || overNode.id !== highlightedRowRef.current) {
                highlightedRowRef.current = overNode.id;
                setHighlightedRow(overNode.id);
            }
        },
        [highlightedRow]
    );

    const getRowStyle = useCallback((params: any) => {
        return params?.node?.data?.id === highlightedRowRef.current
            ? { backgroundColor: 'lightblue' }
            : undefined;
    }, []);

    useEffect(() => {
        if (!tasksGridApi || !routesGridApi) return;

        const dropZoneParams = routesGridApi.getRowDropZoneParams({
            onDragStop,
            onDragLeave: () => {
                highlightedRowRef.current = null;
                setHighlightedRow(null);
                routesGridApi.redrawRows();
            },
        });

        tasksGridApi.removeRowDropZone(dropZoneParams);
        tasksGridApi.addRowDropZone(dropZoneParams);
    }, [routesGridApi, tasksGridApi, onDragStop]);

    const dragAndDropOptionsRoutes = useCallback(
        () => ({
            onRowDragMove,
            getRowStyle,
            rowDragMultiRow: true,
        }),
        [onRowDragMove, getRowStyle]
    );

    const dragAndDropOptionsTasks = useCallback(
        () => ({
            rowDrag: (params: any) =>
                params.colDef.colId === 'placeholder' && params.data?.draggable,
            rowDragText: (_: any, itemCount: number) =>
                `${TranslationHelper.translate('Tasks selected')}: ${
                    itemCount || 1
                }`,
            getRowStyle,
            rowDragMultiRow: true,
        }),
        [getRowStyle]
    );

    return {
        dragAndDropOptionsRoutes,
        dragAndDropOptionsTasks,
        onDragStop,
        highlightedRow,
        setHighlightedRow,
        highlightedRowRef,
    };
};
